import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserEventLogs } from '../../hooks/indexer/useUserEventLogs';
import { formatUsdt, bscTx, shortAddr, toCsv } from '../../lib/format';

const TYPE_META = {
  Deposited: { icon: 'fa-arrow-down', accent: 'gold', sign: '' },
  Claimed: { icon: 'fa-coins', accent: 'emerald', sign: '+' },
  Reinvested: { icon: 'fa-recycle', accent: 'default', sign: '' },
  Withdrawn: { icon: 'fa-arrow-up', accent: 'default', sign: '+' },
  ReferralClaimed: { icon: 'fa-sack-dollar', accent: 'emerald', sign: '+' },
};

function eventAmount(ev) {
  if (!ev?.args) return null;
  switch (ev.type) {
    case 'Deposited':
      return ev.args.amount;
    case 'Claimed':
      return ev.args.netToUser;
    case 'Reinvested':
      return ev.args.reinvested;
    case 'Withdrawn':
      return ev.args.principal;
    case 'ReferralClaimed':
      return ev.args.amount;
    default:
      return null;
  }
}

function eventSummary(ev, t) {
  switch (ev.type) {
    case 'Deposited':
      return t('dashboard.activity.labels.deposited', {
        idx: String(ev.args.depositIdx),
        plan: String(ev.args.planId),
      });
    case 'Claimed':
      return t('dashboard.activity.labels.claimed', { idx: String(ev.args.depositIdx) });
    case 'Reinvested':
      return t('dashboard.activity.labels.reinvested', {
        src: String(ev.args.srcDepositIdx),
        dst: String(ev.args.newDepositIdx),
      });
    case 'Withdrawn':
      return t('dashboard.activity.labels.withdrawn', { idx: String(ev.args.depositIdx) });
    case 'ReferralClaimed':
      return t('dashboard.activity.labels.referral');
    default:
      return ev.type;
  }
}

const Activity = () => {
  const { t } = useTranslation();
  const { data: events = [], isLoading, error, refetch } = useUserEventLogs();

  const exportCsv = () => {
    const rows = events.map((e) => ({
      type: e.type,
      block: String(e.blockNumber),
      timestamp: e.timestamp ? new Date(e.timestamp * 1000).toISOString() : '',
      amount: (() => {
        const a = eventAmount(e);
        return a !== null && a !== undefined ? formatUsdt(a, { maximumFractionDigits: 6 }) : '';
      })(),
      hash: e.hash,
    }));
    const csv = toCsv(rows, [
      { key: 'type', label: 'Type' },
      { key: 'block', label: 'Block' },
      { key: 'timestamp', label: 'Timestamp (UTC)' },
      { key: 'amount', label: 'Amount USDT' },
      { key: 'hash', label: 'Tx Hash' },
    ]);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scrooge-activity-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sorted = useMemo(() => events, [events]);

  return (
    <div className="db-page db-page--activity">
      <div className="db-page__head">
        <h1 className="db-page__title">{t('dashboard.activity.title')}</h1>
        <p className="db-page__subtitle">{t('dashboard.activity.subtitle')}</p>
      </div>

      <div className="db-actions db-actions--small">
        <button
          type="button"
          className="sf-btn sf-btn--outline sf-btn--sm"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <i className="fas fa-rotate" /> {t('dashboard.activity.refresh')}
        </button>
        <button
          type="button"
          className="sf-btn sf-btn--outline sf-btn--sm"
          onClick={exportCsv}
          disabled={!sorted.length}
        >
          <i className="fas fa-file-csv" /> {t('dashboard.activity.exportCsv')}
        </button>
      </div>

      {isLoading && (
        <div className="db-loading">
          <i className="fas fa-circle-notch fa-spin" /> {t('dashboard.common.loading')}
        </div>
      )}
      {error && (
        <div className="db-banner db-banner--warn">
          <i className="fas fa-triangle-exclamation" />
          <div>{t('dashboard.activity.error')}</div>
        </div>
      )}

      {!isLoading && !error && !sorted.length && (
        <div className="db-empty">
          <div className="db-empty__icon">
            <i className="fas fa-clock-rotate-left" />
          </div>
          <h3>{t('dashboard.activity.empty.title')}</h3>
          <p>{t('dashboard.activity.empty.desc')}</p>
        </div>
      )}

      <div className="act-list">
        {sorted.map((ev) => {
          const meta = TYPE_META[ev.type] ?? TYPE_META.Deposited;
          const amt = eventAmount(ev);
          const dt = ev.timestamp ? new Date(ev.timestamp * 1000) : null;
          return (
            <a
              key={`${ev.hash}-${ev.logIndex}`}
              href={bscTx(ev.hash)}
              target="_blank"
              rel="noopener noreferrer"
              className={`act-row act-row--${meta.accent}`}
            >
              <div className="act-row__icon">
                <i className={`fas ${meta.icon}`} />
              </div>
              <div className="act-row__main">
                <div className="act-row__title">{eventSummary(ev, t)}</div>
                <div className="act-row__sub mono">
                  {dt ? dt.toLocaleString() : `Block #${String(ev.blockNumber)}`}
                  {' · '}
                  {shortAddr(ev.hash, 8, 6)}
                </div>
              </div>
              <div className="act-row__amt mono tabular-nums">
                {amt !== null && amt !== undefined ? `${meta.sign}${formatUsdt(amt)} USDT` : ''}
              </div>
              <i className="fas fa-arrow-up-right-from-square act-row__ext" />
            </a>
          );
        })}
      </div>
    </div>
  );
};

export default Activity;
